import React, { useState } from 'react';
import { Card, ListGroup, Button, Badge, Spinner, Collapse } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { signalRService } from '../../services/signalRService';

// Player interface with connection status properties
export interface PlayerListItem {
  id: string | number;
  userId: string;
  displayName?: string;
  playerName: string;
  isHost: boolean;
  isConnected?: boolean;
  lastConnected?: Date;
  lastDisconnected?: Date;
  isActive?: boolean;
  connectionId?: string;
  score?: number;
  correctAnswers?: number;
  totalAnswers?: number;
}

interface PlayerListProps {
  players: PlayerListItem[];
  gameCode?: string;
  gameId?: string | number;
  refreshPlayers: () => Promise<boolean>;
  onRemovePlayer?: (player: PlayerListItem) => void;
  isHost?: boolean;
  className?: string;
  title?: string;
  initiallyCollapsed?: boolean;
}

const PlayerList: React.FC<PlayerListProps> = ({
  players,
  gameCode,
  gameId,
  refreshPlayers,
  onRemovePlayer,
  isHost = false,
  className = '',
  title,
  initiallyCollapsed = false
}) => {
  const { user } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(initiallyCollapsed);

  const handleRefreshPlayers = async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      await refreshPlayers();
      
      // Request connected users from SignalR if gameCode is available
      if (gameCode && gameId) {
        try {
          console.log('Requesting latest connected users from SignalR...');
          if (signalRService.requestConnectedUsers) {
            signalRService.requestConnectedUsers(gameCode).catch(error => {
              console.error('Error requesting connected users from SignalR:', error);
            });
          } else {
            console.error('signalRService.requestConnectedUsers is not defined');
          }
        } catch (signalRError) {
          console.error('Error requesting connected users from SignalR:', signalRError);
        }
      }
    } catch (error) {
      console.error('Error refreshing players:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Card className={`border-0 shadow-sm mb-4 ${className}`}>
      <Card.Body className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0" style={{ cursor: 'pointer' }} onClick={() => setIsCollapsed(!isCollapsed)}>
            <i className={`fas ${isCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'} me-2 text-primary`}></i>
            <i className="fas fa-users me-2 text-primary"></i>
            {title || `Players (${players?.length || 0})`}
          </h5>
          <div>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleRefreshPlayers}
              disabled={isRefreshing}
              className="me-2"
            >
              {isRefreshing ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                  Refreshing...
                </>
              ) : (
                <>
                  <i className="fas fa-sync-alt me-1"></i>
                  Refresh
                </>
              )}
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setIsCollapsed(!isCollapsed)}
            >
              {isCollapsed ? 'Show' : 'Hide'}
            </Button>
          </div>
        </div>

        <Collapse in={!isCollapsed}>
          <div>
            {/* Players list */}
            <ListGroup variant="flush" className="mt-3">
              {players.length === 0 ? (
                <ListGroup.Item className="text-center text-muted">
                  <i className="fas fa-info-circle me-2"></i>
                  No players have joined yet
                </ListGroup.Item>
              ) : (
                players.map((player, index) => (
                  <ListGroup.Item
                    key={player.id || `player-${index}`}
                    className="d-flex justify-content-between align-items-center px-0"
                  >
                    <div className="d-flex align-items-center">
                      <div
                        className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                        style={{ width: '40px', height: '40px', fontSize: '14px', fontWeight: 'bold' }}
                      >
                        {player.playerName ? player.playerName.substring(0, 2).toUpperCase() : 'P?'}
                      </div>
                      <div>
                        <div className="fw-semibold">{player.playerName || 'Unknown Player'}</div>
                        {player.isHost && (
                          <small className="text-muted">
                            <i className="fas fa-crown me-1 text-warning"></i>
                            Game Host
                          </small>
                        )}
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      {player.isHost && (
                        <Badge bg="warning" text="dark" className="me-2">Host</Badge>
                      )}
                      {/* Force host to always show as connected */}
                      <span className={`badge ${(player.isHost || player.isConnected) ? 'bg-success' : 'bg-danger'} d-flex align-items-center`}>
                        {(player.isHost || player.isConnected) ? (
                          <>
                            <span className="spinner-grow spinner-grow-sm me-1" style={{ width: '6px', height: '6px' }}></span>
                            Connected
                          </>
                        ) : (
                          <>
                            <i className="fas fa-times-circle me-1"></i>
                            Disconnected
                            {player.lastDisconnected && (
                              <span className="ms-1 small">
                                {Math.floor((Date.now() - new Date(player.lastDisconnected).getTime()) / 60000)}m ago
                              </span>
                            )}
                          </>
                        )}
                      </span>
                      {isHost && onRemovePlayer && !player.isHost && !player.isConnected && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="ms-2"
                          onClick={() => onRemovePlayer(player)}
                        >
                          <i className="fas fa-user-slash"></i>
                        </Button>
                      )}
                    </div>
                  </ListGroup.Item>
                ))
              )}
            </ListGroup>
          </div>
        </Collapse>
      </Card.Body>
    </Card>
  );
};

export default PlayerList;
